import { EventIdentifier, type GuardStatus, TrackingEventType, useAnalytics, useIntl } from '@capsicum/common';
import { useUkAwareText, worldChampionshipDisclaimerAtom } from '@capsicum/socios-core';
import { useAtom } from 'jotai';
import { useMemo, useState } from 'react';

import { useWCPledgeBonusPercentage } from './useWCPledgeBonusPercentage';

export const useWCPledgeDisclaimer = () => {
	const { $t } = useIntl();
	const { isUkUser } = useUkAwareText();
	const { formattedBonusPercentage } = useWCPledgeBonusPercentage();
	const analytics = useAnalytics();
	const [hasSeenDisclaimer, setHasSeenDisclaimer] = useAtom(worldChampionshipDisclaimerAtom);
	const [isProcessingAcceptance, setIsProcessingAcceptance] = useState(false);

	const status: GuardStatus = useMemo(() => {
		if (isProcessingAcceptance) return 'loading';

		return isUkUser && !hasSeenDisclaimer ? 'denied' : 'allowed';
	}, [isUkUser, hasSeenDisclaimer, isProcessingAcceptance]);

	const onAccepted = (close: (shouldInvokeOnDismiss?: boolean) => void) => {
		close(false);
		setIsProcessingAcceptance(true);
		setHasSeenDisclaimer(true);
		setTimeout(() => {
			setIsProcessingAcceptance(false);
			analytics.sendEvent(TrackingEventType.track, EventIdentifier.UKRegulationLockerRoomDisclaimerClicked);
		}, 100);
	};

	const intlTexts = {
		buttonText: $t('worldChampionshipPledge.disclaimer.buttonText'),
		description: $t('worldChampionshipPledge.disclaimer.description', {
			bonus: formattedBonusPercentage
		}),
		title: $t('worldChampionshipPledge.disclaimer.title')
	};

	return {
		intlTexts,
		onAccepted,
		status
	};
};
